import { CSSProperties, ReactNode } from 'react';

/**
 * AlmaBriefing — Alma's read on an inquiry, shown to hosts in the trip
 * detail panel: a header row (Alma avatar, "Alma · AI assistant", bold
 * headline, optional match score /100), then a checklist of signals and an
 * optional gold caution row.
 *
 * Score and signals are optional — when only a summary is available it
 * renders as headline-only.
 */
export interface AlmaBriefingProps {
  /** Bold one-line read, e.g. "Strong match — cultural depth aligns…". */
  headline: string;
  /** Match score out of 100. Omit to hide the score block. */
  score?: number | null;
  /** Positive signal lines (violet checks). */
  signals?: string[];
  /** Single caution line (gold warning) under a hairline. */
  caution?: string | null;
  /** Alma's photo. Falls back to the cream initial disc. */
  avatarUrl?: string | null;
  /** Custom avatar node (overrides avatarUrl). */
  avatar?: ReactNode;
  style?: CSSProperties;
}

export function AlmaBriefing({ headline, score, signals = [], caution, avatarUrl, avatar, style }: AlmaBriefingProps) {
  const avatarNode = avatar ?? (avatarUrl ? (
    <img src={avatarUrl} alt="Alma" style={{ width: 30, height: 30, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
  ) : (
    <div
      className="avatar-alma"
      style={{ width: 30, height: 30, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontFamily: 'var(--font-serif)', fontSize: 'var(--fs-caption)' }}
    >
      A
    </div>
  ));

  const hasBody = signals.length > 0 || !!caution;

  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, overflow: 'hidden', fontFamily: 'var(--font-sans)', ...style }}>
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '13px 15px', borderBottom: hasBody ? '1px solid var(--line)' : 'none' }}>
        {avatarNode}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 'var(--fs-caption)', color: 'var(--text-dim)', marginBottom: 1 }}>Alma · AI assistant</div>
          <div style={{ fontSize: 'var(--fs-footnote)', color: 'var(--text)', fontWeight: 500, lineHeight: 1.3, textWrap: 'pretty' }}>{headline}</div>
        </div>
        {score != null ? (
          <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            <div style={{ fontFamily: 'var(--font-serif)', fontSize: 'calc(22px * var(--text-scale))', color: 'var(--text)', lineHeight: 1 }}>{score}</div>
            <div style={{ fontSize: 'var(--fs-caption2)', color: 'var(--text-dim)' }}>/ 100</div>
          </div>
        ) : null}
      </div>

      {/* Signals + caution */}
      {hasBody ? (
        <div style={{ padding: '12px 15px', display: 'flex', flexDirection: 'column', gap: 7 }}>
          {signals.map((text) => (
            <div key={text} style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
              <i className="ti ti-check" style={{ fontSize: 'calc(12px * var(--text-scale))', color: 'rgb(var(--violet))', flexShrink: 0, marginTop: 1 }} />
              <span style={{ fontSize: 'var(--fs-caption)', color: 'var(--text-soft)', lineHeight: 1.5, textWrap: 'pretty' }}>{text}</span>
            </div>
          ))}
          {caution ? (
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: signals.length ? 4 : 0, paddingTop: signals.length ? 10 : 0, borderTop: signals.length ? '1px solid var(--line)' : 'none' }}>
              <i className="ti ti-alert-triangle" style={{ fontSize: 'calc(12px * var(--text-scale))', color: 'rgb(var(--gold))', flexShrink: 0, marginTop: 1 }} />
              <span style={{ fontSize: 'var(--fs-caption)', color: 'var(--text-soft)', lineHeight: 1.5, textWrap: 'pretty' }}>{caution}</span>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export default AlmaBriefing;
